import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { Logo } from "../../components/Logo";
import { teamApi, formatErr } from "../../lib/teamApi";

export default function TeamLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submitStep1 = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await teamApi.loginStep1(email.trim(), password);
      setChallenge(r.challenge_token);
      setStep(2);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await teamApi.loginStep2(challenge, otp.trim());
      navigate("/team");
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="absolute top-6 left-6"><Logo /></div>
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong rounded-3xl p-8 w-full max-w-md"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-[#FFF3ED] flex items-center justify-center">
            {step === 1 ? <Lock className="w-4 h-4 text-[#FF5E1A]" /> : <ShieldCheck className="w-4 h-4 text-[#FF5E1A]" />}
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-[#FF5E1A]">
            Team Sign-in · Step {step} of 2
          </span>
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#0F172A] mb-1">
          {step === 1 ? "Welcome back" : "Enter your OTP"}
        </h1>
        <p className="text-sm text-[#475569] mb-6">
          {step === 1
            ? "Sign in with your email and password to continue."
            : "Enter the OTP your admin shared with you (Step 2 of 2)."}
        </p>

        {step === 1 ? (
          <form onSubmit={submitStep1} className="flex flex-col gap-3">
            <input
              type="email" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              data-testid="team-email-input"
              className="w-full px-4 py-3 rounded-2xl glass-input text-sm"
            />
            <input
              type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="team-password-input"
              className="w-full px-4 py-3 rounded-2xl glass-input text-sm"
            />
            {err && <div className="text-xs text-red-600 font-medium" data-testid="team-login-error">{err}</div>}
            <button
              type="submit" disabled={busy}
              data-testid="team-login-step1-submit"
              className="mt-2 rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-3.5 font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <form onSubmit={submitStep2} className="flex flex-col gap-3">
            <div className="text-xs text-slate-500">Signed in as <b>{email}</b></div>
            <input
              type="text" required autoFocus inputMode="numeric"
              value={otp} onChange={(e) => setOtp(e.target.value)}
              placeholder="OTP"
              data-testid="team-otp-input"
              className="w-full px-4 py-3 rounded-2xl glass-input text-sm tracking-widest text-center font-mono"
            />
            {err && <div className="text-xs text-red-600 font-medium" data-testid="team-login-error">{err}</div>}
            <button
              type="submit" disabled={busy}
              data-testid="team-login-step2-submit"
              className="rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-3.5 font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              Sign in <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button" onClick={() => { setStep(1); setOtp(""); setErr(""); }}
              data-testid="team-back-step1"
              className="text-xs text-slate-500 hover:text-[#FF5E1A]"
            >
              ← Back
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
